import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, School as SchoolIcon, CheckCircle2, ArrowRight,
  ArrowLeft, Copy, Check, Loader2, Link2, UserPlus, Sparkles,
  GraduationCap, Bus, Wallet, AlertCircle, ChevronRight,
} from "lucide-react";
import { C, ACCENT_GRADIENT, type Student } from "./tokens";
import { useAuth } from "@/lib/auth-context";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const EASE = [0.4, 0, 0.2, 1] as const;

type School = { id: string; name: string };
type GeneratedInvite = {
  student: { id: string; full_name: string; student_no: string; class_label: string; color_hex: string; school_id: string };
  invitation: { id: string; invite_code: string; expires_at: string; status: string };
  school_name: string;
  invite_link: string;
};

type Step = "choose" | "enter-code" | "redeeming" | "success" | "simulate" | "sim-result";

export function AddChildFromSchoolDialog({ open, onClose, onChildAdded }: {
  open: boolean; onClose: () => void; onChildAdded?: (s: Student) => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("choose");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [linkedStudent, setLinkedStudent] = useState<Student | null>(null);
  const [copied, setCopied] = useState(false);

  // Simulator state
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [simStudentName, setSimStudentName] = useState("");
  const [simClassLabel, setSimClassLabel] = useState("");
  const [simStudentNo, setSimStudentNo] = useState("");
  const [simBoarding, setSimBoarding] = useState(false);
  const [simBusRoute, setSimBusRoute] = useState("");
  const [simGuardianEmail, setSimGuardianEmail] = useState("");
  const [simGuardianPhone, setSimGuardianPhone] = useState("");
  const [generatedInvite, setGeneratedInvite] = useState<GeneratedInvite | null>(null);
  const [loading, setLoading] = useState(false);

  const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/school-student-invite`;

  const reset = useCallback(() => {
    setStep("choose");
    setInviteCode("");
    setError(null);
    setLinkedStudent(null);
    setCopied(false);
    setSelectedSchool(null);
    setSimStudentName("");
    setSimClassLabel("");
    setSimStudentNo("");
    setSimBoarding(false);
    setSimBusRoute("");
    setSimGuardianEmail("");
    setSimGuardianPhone("");
    setGeneratedInvite(null);
  }, []);

  useEffect(() => { if (open) reset(); }, [open, reset]);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ action: "list_schools" }),
      });
      const data = await res.json();
      if (data.ok) setSchools(data.schools);
    } catch { /* ignore */ }
    setLoading(false);
  }, [fnUrl]);

  useEffect(() => { if (step === "simulate" && schools.length === 0) fetchSchools(); }, [step, schools, fetchSchools]);

  const handleRedeem = async () => {
    if (!inviteCode.trim() || !user?.id) return;
    setStep("redeeming");
    setError(null);
    try {
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ action: "redeem_student_invite", invite_code: inviteCode.trim(), user_id: user.id }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error ?? "Failed to redeem invitation");
        setStep("enter-code");
        return;
      }
      // Build Student object from response
      const s = data.student;
      const student: Student = {
        id: s.id,
        full_name: s.full_name,
        initials: s.full_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase(),
        color_hex: s.color_hex ?? "#7B1416",
        student_no: s.student_no,
        class_label: s.class_label,
        boarding: false,
        bus_route: null,
        status_label: "Enrolled",
        status_dot: "success",
        status_location: "Linked to parent",
        balance_ugx: 0,
        daily_cap_ugx: 15000,
        school_name: s.school_name,
        active: true,
      };
      setLinkedStudent(student);
      setStep("success");
      qc.invalidateQueries({ queryKey: ["students"] });
      onChildAdded?.(student);
    } catch {
      setError("Network error. Please try again.");
      setStep("enter-code");
    }
  };

  const handleSimulate = async () => {
    if (!selectedSchool || !simStudentName.trim() || !simClassLabel.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({
          action: "generate_student_invite",
          school_id: selectedSchool.id,
          student_name: simStudentName.trim(),
          class_label: simClassLabel.trim(),
          student_no: simStudentNo.trim() || undefined,
          boarding: simBoarding,
          bus_route: simBusRoute.trim() || undefined,
          guardian_email: simGuardianEmail.trim() || undefined,
          guardian_phone: simGuardianPhone.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error ?? "Failed to generate invite");
        setLoading(false);
        return;
      }
      setGeneratedInvite(data);
      setStep("sim-result");
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  const copyLink = () => {
    if (!generatedInvite) return;
    navigator.clipboard.writeText(generatedInvite.invitation.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(20px)" }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.92, y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
            className="w-full max-w-lg rounded-[28px] shadow-2xl overflow-hidden bg-white max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative overflow-hidden px-6 pt-7 pb-6" style={{ background: ACCENT_GRADIENT }}>
              <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 65%)" }} />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl flex items-center justify-center bg-white/20 backdrop-blur-xl">
                    <UserPlus className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Link Your Student</h2>
                    <p className="text-xs text-white/60">Accept a school invitation to connect your child</p>
                  </div>
                </div>
                <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors">
                  <X className="h-4 w-4 text-white/70" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* STEP: Choose */}
              {step === "choose" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                  <p className="text-sm mb-4" style={{ color: C.textMuted }}>How would you like to add a child?</p>
                  <button onClick={() => setStep("enter-code")} className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] text-left" style={{ borderColor: C.accent, background: C.accentLight }}>
                    <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: ACCENT_GRADIENT }}>
                      <Link2 className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: C.text }}>Enter School Invite Code</p>
                      <p className="text-xs mt-0.5" style={{ color: C.textMid }}>Redeem a code from your child's school</p>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{ color: C.accent }} />
                  </button>
                  <button onClick={() => setStep("simulate")} className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] text-left" style={{ borderColor: C.border, background: "#EDE4D4" }}>
                    <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.pink})` }}>
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold" style={{ color: C.text }}>Simulate School Invite</p>
                      <p className="text-xs mt-0.5" style={{ color: C.textMid }}>Generate a test invite as if sent by a school</p>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{ color: C.textMuted }} />
                  </button>
                </motion.div>
              )}

              {/* STEP: Enter Code */}
              {step === "enter-code" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                  <button onClick={() => setStep("choose")} className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.accent }}>
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Invite Code</label>
                    <input
                      autoFocus
                      value={inviteCode}
                      onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setError(null); }}
                      onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
                      placeholder="ABCD1234"
                      className="w-full mt-2 px-4 py-3.5 rounded-2xl border-2 text-lg font-bold tracking-[0.2em] text-center uppercase outline-none transition-all focus:scale-[1.01]"
                      style={{ borderColor: error ? C.red : C.border, color: C.text, background: "#EDE4D4" }}
                      maxLength={8}
                    />
                    {error && (
                      <div className="flex items-center gap-2 mt-3 p-3 rounded-xl" style={{ background: C.redLight }}>
                        <AlertCircle className="h-4 w-4 shrink-0" style={{ color: C.red }} />
                        <p className="text-xs font-medium" style={{ color: C.red }}>{error}</p>
                      </div>
                    )}
                    <p className="text-xs mt-3" style={{ color: C.textMuted }}>
                      Your child's school generates this code. Enter the 8-character code they shared with you.
                    </p>
                  </div>
                  <button
                    onClick={handleRedeem}
                    disabled={inviteCode.length < 8}
                    className="w-full h-12 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: ACCENT_GRADIENT }}
                  >
                    Link Child <ArrowRight className="h-4 w-4" />
                  </button>
                </motion.div>
              )}

              {/* STEP: Redeeming */}
              {step === "redeeming" && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-10 w-10 animate-spin" style={{ color: C.accent }} />
                  <p className="text-sm font-semibold mt-4" style={{ color: C.text }}>Linking your child...</p>
                </motion.div>
              )}

              {/* STEP: Success */}
              {step === "success" && linkedStudent && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-4">
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="h-20 w-20 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-lg"
                    style={{ background: `linear-gradient(135deg, ${C.green}, ${C.green}cc)` }}
                  >
                    <CheckCircle2 className="h-10 w-10 text-white" />
                  </motion.div>
                  <h3 className="text-xl font-bold tracking-tight" style={{ color: C.text }}>Child Linked!</h3>
                  <p className="text-sm mt-1" style={{ color: C.textMuted }}>{linkedStudent.full_name} is now in your family portal.</p>
                  <div className="mt-6 p-4 rounded-2xl border" style={{ borderColor: C.cardBorder, background: "#EDE4D4" }}>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-white font-bold shrink-0" style={{ background: `linear-gradient(135deg, ${linkedStudent.color_hex}, ${linkedStudent.color_hex}cc)` }}>
                        {linkedStudent.initials}
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: C.text }}>{linkedStudent.full_name}</p>
                        <p className="text-xs" style={{ color: C.textMuted }}>{linkedStudent.school_name} · {linkedStudent.class_label}</p>
                        <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{linkedStudent.student_no}</p>
                      </div>
                    </div>
                  </div>
                  <button onClick={onClose} className="w-full h-12 mt-6 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02]" style={{ background: ACCENT_GRADIENT }}>
                    Done
                  </button>
                </motion.div>
              )}

              {/* STEP: Simulate */}
              {step === "simulate" && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                  <button onClick={() => setStep("choose")} className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: C.accent }}>
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                  <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: C.purpleLight }}>
                    <Sparkles className="h-4 w-4 shrink-0" style={{ color: C.purple }} />
                    <p className="text-xs font-medium" style={{ color: C.purple }}>Simulate a school generating a student invite link. The data belongs to the school.</p>
                  </div>

                  {/* School picker */}
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>School</label>
                    {loading && schools.length === 0 ? (
                      <div className="flex items-center gap-2 mt-2"><Loader2 className="h-4 w-4 animate-spin" style={{ color: C.accent }} /><span className="text-xs" style={{ color: C.textMuted }}>Loading schools...</span></div>
                    ) : (
                      <div className="mt-2 max-h-40 overflow-y-auto rounded-2xl border" style={{ borderColor: C.border, background: "#EDE4D4" }}>
                        {schools.map(s => (
                          <button key={s.id} onClick={() => setSelectedSchool(s)} className="w-full flex items-center gap-3 px-4 py-3 transition-colors text-left" style={selectedSchool?.id === s.id ? { background: C.accentLight } : {}}>
                            <SchoolIcon className="h-4 w-4 shrink-0" style={{ color: selectedSchool?.id === s.id ? C.accent : C.textMuted }} />
                            <span className="text-sm font-medium flex-1 truncate" style={{ color: C.text }}>{s.name}</span>
                            {selectedSchool?.id === s.id && <Check className="h-4 w-4" style={{ color: C.accent }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Student details */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Student Name</label>
                      <input value={simStudentName} onChange={(e) => setSimStudentName(e.target.value)} placeholder="Jane Doe" className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Class</label>
                      <input value={simClassLabel} onChange={(e) => setSimClassLabel(e.target.value)} placeholder="Primary 5" className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Student No (optional)</label>
                      <input value={simStudentNo} onChange={(e) => setSimStudentNo(e.target.value)} placeholder="Auto" className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Bus Route (optional)</label>
                      <input value={simBusRoute} onChange={(e) => setSimBusRoute(e.target.value)} placeholder="Route A" className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Guardian Email (optional)</label>
                      <input value={simGuardianEmail} onChange={(e) => setSimGuardianEmail(e.target.value)} placeholder="parent@email.com" className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>Guardian Phone (optional)</label>
                      <input value={simGuardianPhone} onChange={(e) => setSimGuardianPhone(e.target.value)} placeholder="+256..." className="w-full mt-1.5 px-3 py-2.5 rounded-xl border text-sm outline-none transition-all focus:scale-[1.01]" style={{ borderColor: C.border, color: C.text, background: "#EDE4D4" }} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <button onClick={() => setSimBoarding(!simBoarding)} className="relative h-6 w-11 rounded-full transition-colors" style={{ background: simBoarding ? C.green : C.border }}>
                      <motion.div layout className="h-5 w-5 rounded-full bg-white shadow-sm" style={{ marginLeft: simBoarding ? 22 : 2 }} />
                    </button>
                    <span className="text-sm font-medium" style={{ color: C.textMid }}>Boarding student</span>
                  </label>

                  {error && (
                    <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: C.redLight }}>
                      <AlertCircle className="h-4 w-4 shrink-0" style={{ color: C.red }} />
                      <p className="text-xs font-medium" style={{ color: C.red }}>{error}</p>
                    </div>
                  )}

                  <button
                    onClick={handleSimulate}
                    disabled={!selectedSchool || !simStudentName.trim() || !simClassLabel.trim() || loading}
                    className="w-full h-12 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${C.purple}, ${C.pink})` }}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" />Generate Invite Link</>}
                  </button>
                </motion.div>
              )}

              {/* STEP: Sim Result */}
              {step === "sim-result" && generatedInvite && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }} className="h-16 w-16 rounded-3xl flex items-center justify-center mx-auto shadow-lg" style={{ background: `linear-gradient(135deg, ${C.green}, ${C.green}cc)` }}>
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </motion.div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold tracking-tight" style={{ color: C.text }}>Invite Generated!</h3>
                    <p className="text-xs mt-1" style={{ color: C.textMuted }}>{generatedInvite.school_name} created a student record and invite.</p>
                  </div>

                  {/* Student card */}
                  <div className="p-4 rounded-2xl border" style={{ borderColor: C.cardBorder, background: "#EDE4D4" }}>
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-2xl flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: `linear-gradient(135deg, ${generatedInvite.student.color_hex}, ${generatedInvite.student.color_hex}cc)` }}>
                        {generatedInvite.student.full_name.split(" ").map((n: string) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: C.text }}>{generatedInvite.student.full_name}</p>
                        <p className="text-xs" style={{ color: C.textMuted }}>{generatedInvite.school_name} · {generatedInvite.student.class_label}</p>
                        <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{generatedInvite.student.student_no}</p>
                      </div>
                    </div>
                  </div>

                  {/* Invite code */}
                  <div className="p-4 rounded-2xl border-2" style={{ borderColor: C.accent, background: C.accentLight }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: C.accent }}>Invite Code (share with parent)</p>
                    <div className="flex items-center gap-3">
                      <p className="text-2xl font-bold tracking-[0.3em] flex-1" style={{ color: C.text }}>{generatedInvite.invitation.invite_code}</p>
                      <button onClick={copyLink} className="h-10 w-10 rounded-xl flex items-center justify-center transition-all hover:scale-105" style={{ background: C.accent }}>
                        {copied ? <Check className="h-4 w-4 text-white" /> : <Copy className="h-4 w-4 text-white" />}
                      </button>
                    </div>
                    <p className="text-[10px] mt-2" style={{ color: C.textMuted }}>
                      Expires {new Date(generatedInvite.invitation.expires_at).toLocaleDateString("en-UG", { day: "numeric", month: "short", year: "numeric" })} · Status: {generatedInvite.invitation.status}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => { setInviteCode(generatedInvite.invitation.invite_code); setStep("enter-code"); }} className="flex-1 h-11 rounded-2xl text-sm font-semibold border-2 transition-all hover:scale-[1.02] flex items-center justify-center gap-2" style={{ borderColor: C.accent, color: C.accent }}>
                      <Link2 className="h-4 w-4" /> Redeem Now
                    </button>
                    <button onClick={onClose} className="flex-1 h-11 rounded-2xl text-sm font-semibold text-white transition-all hover:scale-[1.02]" style={{ background: ACCENT_GRADIENT }}>
                      Done
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
